<template>
    <Head :title="t('feedback.responseChat.pageTitle')" />
    <template v-if="useFeedbackChatStore.showLandingPage">
        <FeedbackSwitchingAccounts v-if="isSwitchingAccountsAnimation" @switching-complete="isSwitchingAccountsAnimation = false" />
        <FeedbackResponseLandingPage
            v-else
            :recipient_users_name="recipient_users_name"
            :is-outro="useFeedbackChatStore.isOutro"
            @start-feedback-flow="useFeedbackChatStore.setShowLandingPage(false)" />
    </template>
    <template v-else>
        <div class="h-dvh flex flex-col md:flex-row grow overflow-hidden max-h-full text-ink justify-center">
            <div class="grow flex flex-col overflow-hidden max-h-full">
                <!-- new chat message list -->
                <div ref="chatMessageList" class="grow md:flex snap-y scroll-pb-5 md:scroll-pb-10 overflow-x-hidden">
                    <div class="w-full chat_component rounded-xl md:max-w-(--breakpoint-md) md:mx-auto">
                        <div class="blurEffect"></div>
                        <div class="messages px-4 sm:mt-10 md:px-10">
                            <div class="relative">
                                <div class="fs-mask">
                                    <template v-for="(message, idx) in messages" :key="idx">
                                        <template v-if="message.role !== 'tool'">
                                            <template v-for="(line, lineIdx) in message.lines" :key="lineIdx">
                                                <div
                                                    v-if="shouldRenderLine(line)"
                                                    class="chat group/chat"
                                                    :class="showAsAssistantMessage(message, line) ? 'chat-start nadia-chat' : 'chat-end body'">
                                                    <div
                                                        class="flex gap-2"
                                                        :class="{
                                                            'flex-row': message.role === 'user',
                                                            'flex-col md:flex-row': message.role === 'assistant',
                                                        }">
                                                        <div v-if="showAsAssistantMessage(message, line)" class="min-w-12 max-w-12 min-h-12 max-h-12 -mb-4 md:mb-0 mt-4 -mr-2">
                                                            <CoachingModeMarble />
                                                        </div>
                                                        <div
                                                            v-if="shouldRenderLineContent(line)"
                                                            class="max-w-full px-4 prose-p:my-4 relative ease-in-out duration-500"
                                                            :class="{
                                                                'text-left rounded-2xl border border-border': !showAsAssistantMessage(message, line),
                                                                'bg-surface-raised': !showAsAssistantMessage(message, line) && message.chat_message_id !== focusedMessage,
                                                            }">
                                                            <article class="prose w-full text-left">
                                                                <ChatMessageText
                                                                    v-if="line.type === 'md'"
                                                                    :message="line.content"
                                                                    :message-id="message.chat_message_id"
                                                                    :animate="shouldAnimateMessageText(message, idx)"
                                                                    @finished-rendering="handleLineRendered(message, idx, lineIdx)" />
                                                            </article>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                        </template>
                                    </template>
                                </div>
                                <div ref="bottomAnchor" class="pb-10 md:pb-10 h-[calc(100dvh-400px)]" />
                            </div>
                            <Transition name="fade">
                                <div v-if="!isBottomVisible" class="h-0 sticky bottom-2">
                                    <BaseButton theme="neutral" size="sm" class="absolute w-fit bottom-0 left-1/2 -translate-x-1/2" @click="scrollToLastMessage">
                                        {{ t("feedback.responseChat.scrollButton") }} <i class="bi bi-chevron-down icon-bold" aria-hidden="true"></i>
                                    </BaseButton>
                                </div>
                            </Transition>
                        </div>
                    </div>
                </div>
                <div v-if="messages.length > 0" class="mx-auto mb-6 w-80">
                    <ContextMeterBase
                        :percentage="progressPercentage"
                        :current-checkpoint="currentCheckpointIndex"
                        :message-stack="progressMessages"
                        :aria-label="t('feedback.responseChat.progressLabel')"
                        class="feedback-progress-bar" />
                </div>
                <div class="w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto mb-6 px-2">
                    <ChatMessageControls
                        v-if="useFeedbackChatStore.showChatMessageControls"
                        :location="CHAT_MESSAGE_CONTROLS_LOCATION.FEEDBACK_CHAT"
                        :is-sending="waiting_for_assistant"
                        :is-transcribing="isTranscribing"
                        :is-disabled="showStartLoadMessage"
                        :hide-chat-voice-input-enabled="hide_chat_voice_input_enabled"
                        @text-send="handleSendMessage"
                        @recording-complete="handleRecordingComplete"
                        @recording-error="chatError = CHAT_ERROR.RECORDING_DEFAULT"
                        @recording-start="chatError = null"
                        @text-keydown="chatError = null" />
                </div>
            </div>
        </div>
    </template>
</template>

<script setup>
import { Head } from "@inertiajs/vue3";
import { useElementVisibility } from "@vueuse/core";
import jwtToken from "~vue/api/token.js";
import ChatMessageControls from "~vue/ChatDetail/ChatMessageControls.vue";
import ChatMessageText from "~vue/ChatMessageText.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { useRecorder } from "~vue/composables/useRecorder";
import { CHAT_EVENT } from "~vue/events";
import FeedbackResponseLandingPage from "~vue/Feedback/FeedbackResponseLandingPage.vue";
import ContextMeterBase from "~vue/Onboarding/ContextMeterBase.vue";
import { createRealtimeClient } from "~vue/RealtimeClientFactory.js";
import { useChatStore } from "~vue/stores/chatStore";
import { useFeedbackChatStore } from "~vue/stores/feedbackChatStore";
import { CHAT_ERROR, CHAT_MESSAGE_CONTROLS_LOCATION } from "~vue/utils/constants.js";
import { createWebSocketErrorHandler } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, provide, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { ACTION, lineIsAction } from "../chatActions.js";
import FeedbackSwitchingAccounts from "../Feedback/FeedbackSwitchingAccounts.vue";

const { $setEventContext, $sendSocketEvent, emitter } = inject("globalProperties");
const { t } = useI18n();

const props = defineProps({
    language_options: {
        type: Array,
        default: () => [],
    },
    event_context: { type: Object, default: () => ({}) },
    stream_url: { type: String, default: "" },
    cs_id: { type: String, default: "" },
    cs_uuid: { type: String, default: null },
    recipient_users_name: { type: String, default: "" },
    feedback_question: { type: String, default: "" },
    show_landing_page: {
        type: Boolean,
        default: false,
    },
    show_switching_accounts: {
        type: Boolean,
        default: false,
    },
    already_submitted: {
        type: Boolean,
        default: false,
    },
    hide_chat_voice_input_enabled: {
        type: Boolean,
        default: false,
    },
});

provide("languageOptions", props.language_options);
provide("feedbackQuestion", props.feedback_question);
provide("coachingSessionId", props.cs_id);
provide("coachingSessionUuid", props.cs_uuid);

// Configure the token module to use the anonymous feedback token endpoint
// so that logUserInteraction and other coach_api calls work in the feedback flow.
if (props.cs_uuid) {
    jwtToken.setFeedbackSession(props.cs_uuid);
}

const messages = ref([]);
const socketClient = ref(null);
const dictated_in_this_message = ref(false);
const chatError = ref(null);
const isInitialized = ref(false);
const waiting_for_assistant = ref(false);
const isSwitchingAccountsAnimation = ref(false);

const userCoachExchangeId = ref(crypto.randomUUID());
const numberOfMessagesAtInit = ref(0);
const renderedLines = ref(new Set());

const bottom = useTemplateRef("bottomAnchor");
const isBottomVisible = useElementVisibility(bottom);

const currentPhase = computed(() => {
    const assistantMessagesWithPhase = messages.value.filter((msg) => msg.role === "assistant" && !!msg.phase);
    return assistantMessagesWithPhase[assistantMessagesWithPhase.length - 1]?.phase;
});

// Each phase's checkpoint indexes into progressMessages.
const phaseMapping = {
    intro_and_setup: { checkpoint: 0, progressPercentage: 8 },
    rating_request: { checkpoint: 0, progressPercentage: 16 },
    rating_rationale: { checkpoint: 1, progressPercentage: 25 },
    exploring_examples: { checkpoint: 1, progressPercentage: 33 },
    exploring_behaviors: { checkpoint: 2, progressPercentage: 42 },
    exploring_impact: { checkpoint: 2, progressPercentage: 50 },
    exploring_suggestions: { checkpoint: 2, progressPercentage: 58 },
    final_reflection: { checkpoint: 3, progressPercentage: 66 },
    remaining_insights_check: { checkpoint: 4, progressPercentage: 75 },
    summary_presentation: { checkpoint: 4, progressPercentage: 83 },
    summary_iteration: { checkpoint: 4, progressPercentage: 91 },
    closing: { checkpoint: 4, progressPercentage: 100 },
};

const progressMessages = computed(() => [
    t("feedback.responseChat.progress.warmingUp"),
    t("feedback.responseChat.progress.makingProgress"),
    t("feedback.responseChat.progress.gainingClarity"),
    t("feedback.responseChat.progress.finalStretch"),
    t("feedback.responseChat.progress.closingThingsOut"),
]);

const currentCheckpointIndex = computed(() => {
    const phaseData = currentPhase.value ? phaseMapping[currentPhase.value] : null;
    return phaseData?.checkpoint ?? -1;
});

const progressPercentage = computed(() => {
    const phaseData = currentPhase.value ? phaseMapping[currentPhase.value] : null;
    return phaseData?.progressPercentage ?? 0;
});

// Only animate a message from the LLM that is being streamed in real-time.
const shouldAnimateMessageText = (message, messageIndex) => {
    return message.role === "assistant" && messageIndex >= numberOfMessagesAtInit.value && messageIndex >= messages.value.length - 1;
};

const getMessageIdLineIdxStr = (messageId, lineIdx) => `${messageId}-${lineIdx}`;

const handleLineRendered = (message, _messageIndex, lineIdx) => {
    renderedLines.value.add(getMessageIdLineIdxStr(message.id, lineIdx));
};

const showAsAssistantMessage = (message, line) => {
    return message.role === "assistant" || (lineIsAction(line) && line.action_name === ACTION.LOADING);
};

const sendMessage = (content, { uce_id } = {}) => {
    uce_id = uce_id ?? cycleUserCoachExchangeId();
    if (content.length > 0) {
        waiting_for_assistant.value = true;
        // Temporary message so the backend can send back data about this message
        const temp_id = `${Date.now()}${props.cs_id}`;
        const message = {
            temp_id: temp_id,
            uce_id,
            content,
            role: "user",
            dictation_on: useChatStore.dictationOn,
            coaching_session_id: props.cs_id,
            cs_uuid: props.cs_uuid,
            dictated: dictated_in_this_message.value,
            anon_user: true,
        };

        $sendSocketEvent("user_chat_message", message, socketClient.value);

        message.lines = [{ type: "md", content }];
        messages.value.push(message);
        scrollToBottom();

        dictated_in_this_message.value = false;
    }
};

const isMessageLine = (line) => line.type !== "action";

const shouldRenderLine = (line) => {
    return line.type !== "tool" && (isMessageLine(line) || !isToolOrLoadingAction(line));
};

const handleSendMessage = ({ content }) => {
    sendMessage(content);
};

const shouldRenderLineContent = (line) => {
    /*
     * The LOADING and TOOL_CALL actions are the only actions that are rendered inline as part of the chat message.
     */
    return !isToolOrLoadingAction(line) || !lineIsAction(line);
};

const isToolOrLoadingAction = (line) => {
    return lineIsAction(line) && [ACTION.TOOL_CALL, ACTION.LOADING].includes(line.action_name);
};
const getMessageById = (message_id, temp_id = undefined) => {
    for (let m of messages.value) {
        // TODO: Clean up the keys here
        if (m.id == message_id || m.message_id == message_id || m.chat_message_id == message_id) {
            return m;
        }
        if (temp_id && (m.temp_id == temp_id || m.message_id == temp_id)) {
            return m;
        }
    }
    return false;
};

const cycleUserCoachExchangeId = () => {
    userCoachExchangeId.value = crypto.randomUUID();
    return userCoachExchangeId.value;
};

const handleChatMessage = ({ data }) => {
    const m = getMessageById(data.message_id, data.temp_id);
    if (m) {
        m.lines = data.lines;
        if (data.message_id) {
            m.id = data.message_id;
        }
        if (data.chat_message_id) {
            m.chat_message_id = data.chat_message_id;
        }
        if (data.phase) {
            m.phase = data.phase;
        }
    } else {
        messages.value.push(data);
    }

    if (data.role === "assistant") {
        waiting_for_assistant.value = false;
    }
};

const handleChatMessageRetracted = ({ data }) => {
    messages.value = messages.value.filter((m) => m.message_id != data.temp_id);
};

const scrollToLastMessage = () => {
    /*
     * We target an empty div at the end of the message list. This
     * div has some padding to add additional space when scrolling.
     * This is done to prevent cutting off the last message right
     * at the edge of the scrollable area.
     *
     * Why not target the last message instead? Besides having to maintain additional logic to
     * retrieve the DOM element of the last message, we want to keep the aforementioned
     * spacing without adding/removing it from individual message elements.
     */

    bottom.value?.scrollIntoView({ behavior: "smooth", block: "end" });
};

const chatMessageListRef = useTemplateRef("chatMessageList");
const scrollToBottom = () => {
    /*
     * Delegate scroll to the next tick as this function
     * is invoked at the same time that messages state is mutated.
     * Our scrolling logic depends on DOM elements, so we make sure
     * that this runs when Vue has reconciled the DOM state.
     */
    nextTick(() => {
        if (chatMessageListRef.value) {
            scrollToLastMessage();
        }
    });
};

const initChatWs = () => {
    $setEventContext(props.event_context);
    if (props.stream_url) {
        socketClient.value = createRealtimeClient(props.stream_url, {
            reconnectEnabled: true,
            reconnectInterval: 2000,
        });
        socketClient.value.connect();
        let firstMessageReceived = false;
        socketClient.value.onMessage = (msg) => {
            const message_data = JSON.parse(msg.data);
            if (!firstMessageReceived) {
                firstMessageReceived = true;
            }
            if (message_data.type) {
                handleMessageDataByType(message_data.type, message_data);
            }
        };
        socketClient.value.onOpen = () => {
            if (!isInitialized.value) {
                $sendSocketEvent(
                    "initialize_chat",
                    {
                        dictation_on: useChatStore.dictationOn,
                        uce_id: cycleUserCoachExchangeId(),
                        anon_user: true,
                    },
                    socketClient.value,
                );
            }
            chatError.value = null;
        };
        socketClient.value.onClose = (closeEvent) => {
            if (closeEvent.code === 4401) {
                chatError.value = t("feedback.responseChat.loggedOutError");
            } else {
                chatError.value = t("feedback.responseChat.connectionError");
            }
            $setEventContext({});
        };
        socketClient.value.onError = createWebSocketErrorHandler({ cs_id: props.cs_id, userCoachExchangeId }, "FeedbackResponseChat", () => {
            chatError.value = t("feedback.responseChat.connectionError");
        });
    }
};

const handleMessageDataByType = (type, data) => {
    switch (type) {
        case "initialize_chat":
            handleChatInit(data);
            break;
        case "chat_message":
            handleChatMessage(data);
            break;
        case "chat_partial_addition":
            handleChatPartialAddition(data);
            break;
        case "chat_message_retracted":
            handleChatMessageRetracted(data);
            break;
        case "chat_send_deduplicated":
            waiting_for_assistant.value = false;
            break;
    }
};

const handleChatInit = (data) => {
    messages.value = data.data.messages;
    numberOfMessagesAtInit.value = data.data.messages.length;
    scrollToBottom();
    // Store initialized state so that we don't reinitialize if we disconnect and reconnect
    isInitialized.value = true;
};

const handleChatPartialAddition = (data) => {
    waiting_for_assistant.value = true;

    const m = getMessageById(data.data["message_id"]);

    if (m) {
        // Handling partial content - update all properties including phase
        m.lines = data.data["lines"];
        if (data.data["phase"]) {
            m.phase = data.data["phase"];
        }
    } else {
        messages.value.push(data.data);
    }
};

const { input, handleTranscribe, isTranscribing } = useRecorder();
const handleRecordingComplete = async (recording) => {
    if (recording?.blob) {
        await handleTranscribe(recording);
        if (input.value.length > 0 && waiting_for_assistant.value) {
            // Don't abort the reply in flight: leave the transcript in the composer to send once it lands.
            emitter.emit(CHAT_EVENT.SET_MESSAGE_TEXT, input.value);
        } else if (input.value.length > 0) {
            sendMessage(input.value);
        }
    } else {
        chatError.value = CHAT_ERROR.RECORDING_DEFAULT;
    }
};

onMounted(() => {
    // IF already submitted then just show them the thank you page.
    if (props.already_submitted) {
        useFeedbackChatStore.setShowLandingPage(true);
        useFeedbackChatStore.setIsOutro(true);
    } else {
        isSwitchingAccountsAnimation.value = props.show_switching_accounts;
        initChatWs();
        useFeedbackChatStore.setShowLandingPage(props.show_landing_page);
    }
});

onUnmounted(() => {
    socketClient.value?.removeListeners();
    socketClient.value?.close();
    jwtToken.clearFeedbackSession();
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
.chat_component .chat {
    :deep(article.prose p) {
        word-break: break-word;
    }
}

.chat_component .messages {
    height: 100%;
    max-height: 80vh;
}

.blurEffect {
    @apply z-10 sticky h-16 top-0 left-0 w-full;
    background: linear-gradient(180deg, var(--color-surface-raised), transparent);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
/* Override ContextMeterBase width for feedback progress */
.feedback-progress-bar :deep(.contextMeter) {
    width: 100% !important;
}
</style>
